import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import moment from 'moment';
import { API } from '../../Common/Api';
import CommonTable from '../../Common/CommonTable';
import { ErrorToast, SuccessToast } from '../../Utils/toast';
import { getRole } from '../../Utils/auth';
import TextInput from '../../Commponent/TextInput';
import CustomSelectInput from '../../Commponent/SelectInput';
import { Button } from '../../Common/Button';

// Role-aware support screen:
//  - distributor/dealer/agent: raise a ticket + see their own tickets.
//  - admin: see all tickets and resolve (close) them.
const Support = () => {
  const role = getRole();
  const isAdmin = role === 'admin';

  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');

  // Raise-ticket modal (non-admin)
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Resolve modal (admin)
  const [resolveTicket, setResolveTicket] = useState(null);
  const [remark, setRemark] = useState('');
  const [resolving, setResolving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const statusOptions = [
    { name: 'All Status', value: '' },
    { name: 'Open', value: 'Open' },
    { name: 'Resolved', value: 'Resolved' },
  ];

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 700);
    return () => clearTimeout(t);
  }, [search]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await API.GetSupportTickets({
        page: currentPage,
        limit: itemsPerPage,
        status,
        search: debouncedSearch,
      });
      if (res?.status === 200 && res?.success) {
        setTickets(Array.isArray(res.data) ? res.data : []);
        const t = res.pagination?.total || 0;
        setTotal(t);
        setTotalPages(res.pagination?.totalPages || Math.max(1, Math.ceil(t / itemsPerPage)));
      } else {
        setTickets([]); setTotal(0); setTotalPages(1);
        if (res?.message) ErrorToast(res.message);
      }
    } catch (e) {
      setTickets([]); setTotal(0); setTotalPages(1);
      ErrorToast('Failed to fetch support tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, status, debouncedSearch]);

  const handleRaise = async () => {
    if (!subject.trim() || !description.trim()) {
      ErrorToast('Please enter a subject and description');
      return;
    }
    setSubmitting(true);
    try {
      const res = await API.CreateSupportTicket({ subject, description, vehicleNumber });
      if ((res?.status === 201 || res?.status === 200) && res?.success !== false) {
        SuccessToast(res?.message || 'Support ticket raised successfully');
        setRaiseOpen(false);
        setSubject(''); setDescription(''); setVehicleNumber('');
        setCurrentPage(1);
        fetchTickets();
      } else {
        ErrorToast(res?.message || 'Failed to raise ticket');
      }
    } catch (e) {
      ErrorToast('Failed to raise ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async () => {
    if (!resolveTicket) return;
    setResolving(true);
    try {
      const res = await API.CloseSupportTicket(resolveTicket._id, { remark });
      if (res?.status === 200 && res?.success) {
        SuccessToast(res?.message || 'Ticket resolved');
        setResolveTicket(null);
        setRemark('');
        fetchTickets();
      } else {
        ErrorToast(res?.message || 'Failed to resolve ticket');
      }
    } catch (e) {
      ErrorToast('Failed to resolve ticket');
    } finally {
      setResolving(false);
    }
  };

  const StatusBadge = ({ s }) => {
    const base = 'px-2 py-[2px] rounded-full text-xs font-medium';
    if (s === 'Resolved') return <span className={`${base} bg-green-100 text-green-900`}>Resolved</span>;
    return <span className={`${base} bg-yellow-100 text-yellow-900`}>Open</span>;
  };

  const columns = [
    { label: 'Ticket ID', render: (r) => (r._id ? r._id.slice(-8).toUpperCase() : 'N/A') },
    ...(isAdmin ? [
      {
        label: 'Raised By',
        render: (r) => (
          <div>
            <div className="font-medium text-gray-900">{r.username || r.user?.username || 'N/A'}</div>
            <div className="text-xs text-gray-500 capitalize">{r.role || r.user?.role || ''}</div>
          </div>
        ),
      },
      { label: 'Mobile', render: (r) => r.mobileNo || r.user?.mobileNo || 'N/A' },
      { label: 'Email', render: (r) => r.email || r.user?.email || 'N/A' },
    ] : []),
    {
      label: 'Vehicle Number',
      render: (r) => (
        <div className="font-medium text-gray-900">{r.vehicleNumber || '—'}</div>
      ),
    },
    {
      label: 'Subject',
      tdClassName: '!whitespace-normal',
      render: (r) => (
        <div className="max-w-[220px] break-words" title={r.subject || ''}>{r.subject || 'N/A'}</div>
      ),
    },
    {
      label: 'Description',
      tdClassName: '!whitespace-normal',
      render: (r) => {
        const isExpanded = expandedId === r._id;
        return (
          <div 
            onClick={() => setExpandedId(isExpanded ? null : r._id)}
            className={`max-w-[360px] break-words text-gray-600 cursor-pointer transition-all ${!isExpanded ? 'line-clamp-2' : ''}`}
            title={!isExpanded ? 'Click to read more' : 'Click to collapse'}
          >
            {r.description || ''}
          </div>
        );
      },
    },
    { label: 'Status', render: (r) => <StatusBadge s={r.status} /> },
    { label: 'Raised Date', render: (r) => (r.createdAt ? moment(r.createdAt).format('DD MMM, YYYY hh:mm A') : '-') },
    ...(isAdmin ? [{
      label: 'Action',
      tdClassName: 'flex justify-end',
      render: (r) => (
        r.status === 'Resolved'
          ? <span className="text-xs text-gray-500">Resolved{r.resolvedAt ? ` • ${moment(r.resolvedAt).format('DD MMM')}` : ''}</span>
          : (
            <Button
              label="Resolve"
              onClick={() => { setResolveTicket(r); setRemark(''); }}
              roundedButton
              style={{ backgroundColor: '#22C55E' }}
              customClass="!text-white !px-4 !py-1"
            />
          )
      ),
    }] : [{
      label: 'Admin Remark',
      render: (r) => (r.status === 'Resolved' ? (r.adminRemark || '—') : '—'),
    }]),
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 mb-[16px] mt-[27.5px]">Support</h2>
        {!isAdmin && (
          <Button
            label="Raise Ticket +"
            onClick={() => setRaiseOpen(true)}
            roundedButton
            style={{ backgroundColor: '#EF4444' }}
            customClass="!text-white"
          />
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div className="relative">
            <TextInput
              name="search"
              placeholder={isAdmin ? 'Search subject / name / mobile' : 'Search subject'}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              inputClass="pl-5 pr-10 py-3 text-sm"
            />
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Search className="w-4 h-4" />
            </div>
          </div>
          <CustomSelectInput
            name="status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
            options={statusOptions}
            inputClass="py-3 px-3 text-sm"
          />
        </div>
      </div>

      <div className="p-0 space-y-8">
        <CommonTable
          columns={columns}
          data={tickets}
          emptyMessage={loading ? 'Loading...' : 'No Support Tickets Found'}
          isPagination={true}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          setItemsPerPage={setItemsPerPage}
          total={total}
          totalPages={totalPages}
          loading={loading}
          hideDateAndDownload={true}
        />
      </div>

      {/* Raise Ticket modal */}
      {raiseOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Raise a Support Ticket</h3>
              <button onClick={() => setRaiseOpen(false)} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of your issue"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Number (Optional)</label>
                <input
                  type="text"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  placeholder="e.g. MH01AB1234"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your issue in detail"
                  rows={4}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button onClick={() => setRaiseOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
              <Button
                label={submitting ? 'Submitting...' : 'Submit'}
                onClick={handleRaise}
                disabled={submitting}
                style={{ backgroundColor: '#EF4444' }}
                customClass="!text-white"
              />
            </div>
          </div>
        </div>
      )}

      {/* Resolve modal (admin) */}
      {resolveTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-lg">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Resolve Ticket</h3>
              <button onClick={() => setResolveTicket(null)} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="text-sm text-gray-600">
                <div><span className="text-gray-500">Raised By:</span> <b>{resolveTicket.username}</b> ({resolveTicket.role})</div>
                <div><span className="text-gray-500">Subject:</span> {resolveTicket.subject}</div>
                <div className="mt-3 p-3 bg-gray-50 rounded-md whitespace-pre-wrap break-words max-h-48 overflow-y-auto border border-gray-100">
                  <div className="text-gray-500 text-xs font-semibold uppercase mb-1">Description</div>
                  {resolveTicket.description}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Remark (optional)</label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Add a note that will be included in the notification"
                  rows={3}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <p className="text-xs text-gray-500">On resolve, the user is notified by email &amp; SMS (if available).</p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button onClick={() => setResolveTicket(null)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md">Cancel</button>
              <Button
                label={resolving ? 'Resolving...' : 'Resolve & Notify'}
                onClick={handleResolve}
                disabled={resolving}
                style={{ backgroundColor: '#22C55E' }}
                customClass="!text-white"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Support;
